'use client';

import { useState } from 'react';
import styles from './ReviewForm.module.scss';
import { useTranslations } from 'next-intl';

interface ReviewFormProps {
  parkId: string;
  parkType: 'bikepark' | 'pumptrack' | 'snowpark';
  parkName: string;
}

export default function ReviewForm({ parkId, parkType, parkName }: ReviewFormProps) {
  const t = useTranslations('reviewForm');
  const [formData, setFormData] = useState({
    authorName: '',
    authorEmail: '',
    rating: 5,
    title: '',
    content: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      // Build the review data with proper relation format
      const reviewData: any = {
        ...formData,
        status: 'pending',
      };

      // Add the correct relation based on park type
      // Use connect syntax for Strapi v5
      reviewData[parkType] = {
        connect: [{ documentId: parkId }]
      };

      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/reviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: reviewData,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit review');
      }

      setSubmitStatus('success');
      setFormData({
        authorName: '',
        authorEmail: '',
        rating: 5,
        title: '',
        content: '',
      });
    } catch (error) {
      console.error('Error submitting review:', error);
      setSubmitStatus('error');
      setErrorMessage(t('errorMessage'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'rating' ? parseInt(value) : value,
    }));
  };

  return (
    <div className={styles.reviewForm}>
      <h3 className={styles.title}>{t('title', { parkName })}</h3>

      {submitStatus === 'success' && (
        <div className={styles.successMessage}>
          {t('successMessage')}
        </div>
      )}

      {submitStatus === 'error' && (
        <div className={styles.errorMessage}>
          {errorMessage}
        </div>
      )}

      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.formGroup}>
          <label htmlFor="authorName" className={styles.label}>
            {t('name')}
          </label>
          <input
            type="text"
            id="authorName"
            name="authorName"
            value={formData.authorName}
            onChange={handleChange}
            required
            minLength={2}
            maxLength={100}
            className={styles.input}
            placeholder={t('namePlaceholder')}
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="authorEmail" className={styles.label}>
            {t('email')}
          </label>
          <input
            type="email"
            id="authorEmail"
            name="authorEmail"
            value={formData.authorEmail}
            onChange={handleChange}
            required
            className={styles.input}
            placeholder={t('emailPlaceholder')}
          />
          <small className={styles.hint}>{t('emailHint')}</small>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="rating" className={styles.label}>
            {t('rating')}
          </label>
          <div className={styles.ratingContainer}>
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                className={`${styles.star} ${formData.rating >= star ? styles.starActive : ''}`}
                onClick={() => setFormData((prev) => ({ ...prev, rating: star }))}
                aria-label={t('ratingLabel', { rating: star })}
              >
                ★
              </button>
            ))}
            <span className={styles.ratingValue}>{formData.rating}/5</span>
          </div>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="title" className={styles.label}>
            {t('reviewTitle')}
          </label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            minLength={5}
            maxLength={200}
            className={styles.input}
            placeholder={t('reviewTitlePlaceholder')}
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="content" className={styles.label}>
            {t('content')}
          </label>
          <textarea
            id="content"
            name="content"
            value={formData.content}
            onChange={handleChange}
            required
            minLength={20}
            maxLength={2000}
            rows={6}
            className={styles.textarea}
            placeholder={t('contentPlaceholder')}
          />
          <small className={styles.hint}>
            Minimum 20 znaków, {formData.content.length}/2000
          </small>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className={styles.submitButton}
        >
          {isSubmitting ? t('submitting') : t('submit')}
        </button>
      </form>
    </div>
  );
}
